import { getPositionLabel } from './positionColors';

/**
 * Compact scorecard position labels for org matrix cells.
 */
export function formatCompactScorecardPosition(
  position: string | null | undefined
): string | null {
  if (!position) return null;
  switch (position) {
    case 'Support':
      return 'Sup';
    case 'StrSupport':
      return 'Sup+';
    case 'Oppose':
      return 'Opp';
    case 'StrOppose':
      return 'Opp+';
    default:
      return getPositionLabel(position);
  }
}

export type ScorecardVoteTooltipInput = {
  billPosition?: string | null;
  vote?: string | null;
  aligned?: boolean;
};

/**
 * Single consistent hover tooltip for a scorecard vote cell.
 * - Aligned with {position}
 * - Not aligned with {position}
 * - No final vote · {position}
 */
export function getScorecardVoteTooltip(
  vote: ScorecardVoteTooltipInput | null | undefined
): string | undefined {
  if (!vote?.billPosition) return undefined;
  const positionLabel = getPositionLabel(vote.billPosition);
  if (!vote.vote) return `No final vote · ${positionLabel}`;
  if (vote.aligned) return `Aligned with ${positionLabel}`;
  return `Not aligned with ${positionLabel}`;
}

/**
 * Authenticated + public score color tiers (keep in sync everywhere).
 * ≥80 success, ≥50 warning, else error; null → tertiary.
 */
export function getScorecardScoreColorClass(score: number | null): string {
  if (score === null) return 'text-text-tertiary dark:text-text-dark-tertiary';
  if (score >= 80) return 'text-success-600 dark:text-success-400';
  if (score >= 50) return 'text-warning-600 dark:text-warning-400';
  return 'text-error-600 dark:text-error-400';
}

/**
 * Org bill headers show a position only when every workspace agrees.
 * Workspace mode returns the bill's own position.
 */
export function getScorecardBillHeaderPosition(
  bill: {
    billPosition?: string | null;
    workspaces?: { position?: string | null }[] | null;
  },
  isOrgMode: boolean
): string | null {
  if (!isOrgMode) return bill.billPosition || null;
  const positions = (bill.workspaces || [])
    .map((w) => w.position)
    .filter((p): p is string => !!p);
  if (positions.length === 0) return bill.billPosition || null;
  const unique = [...new Set(positions)];
  return unique.length === 1 ? unique[0] : null;
}
